import { useState, useEffect, useMemo, useRef } from 'react';
import { DialogShell } from './ui/DialogShell';
import { Select } from './ui/Select';
import { TextInput } from './ui/TextInput';
import { ProvinciaEclesiastica, Diocesis, provinciaEclesiasticaService, diocesisService, lumisialService } from '../services';
import { Lumisial } from '../lib/supabase';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { StateSelector } from './location/StateSelector';
import { CitySelector } from './location/CitySelector';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ModalFormActions } from './ui/ModalFormActions';
import type { State, City, Country } from '../types/location';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';

interface LumisialModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
  readonly lumisial?: Lumisial;
}

export function LumisialModal({ isOpen, onClose, onSuccess, lumisial }: LumisialModalProps) {
  const [formData, setFormData] = useState({
    nombre: '',
    provincia_eclesiastica_id: '',
    diocesis_id: '',
    activo: true
  });

  // Datos jerárquicos
  const [provinciasEclesiasticas, setProvinciasEclesiasticas] = useState<ProvinciaEclesiastica[]>([]);
  const [diocesisList, setDiocesisList] = useState<Diocesis[]>([]);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<Pick<City, 'id' | 'name' | 'latitude' | 'longitude'> | null>(null);

  // Loading
  const [loading, setLoading] = useState(false);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingDiocesis, setLoadingDiocesis] = useState(false);

  const { error, clearError, handleError } = useErrorHandler();

  // País derivado de la provincia eclesiástica seleccionada
  const selectedProvincia = useMemo(
    () => provinciasEclesiasticas.find(p => p.id === formData.provincia_eclesiastica_id),
    [provinciasEclesiasticas, formData.provincia_eclesiastica_id]
  );

  const countryForSelectors = useMemo<Pick<Country, 'id' | 'name' | 'iso2'> | null>(() => {
    if (!selectedProvincia?.codigo_iso) return null;
    return { id: '', name: selectedProvincia.nombre, iso2: selectedProvincia.codigo_iso };
  }, [selectedProvincia]);

  const hasFormChanges = (): boolean => {
    if (!lumisial) {
      return formData.nombre !== '' || formData.provincia_eclesiastica_id !== '' || formData.diocesis_id !== '' || selectedState !== null || selectedCity !== null;
    }
    return formData.nombre !== lumisial.nombre ||
      formData.provincia_eclesiastica_id !== lumisial.provincia_eclesiastica_id ||
      formData.diocesis_id !== lumisial.diocesis_id ||
      formData.activo !== lumisial.activo;
  };

  // Cargar provincias al abrir
  useEffect(() => {
    if (isOpen) {
      cargarProvincias();
      if (lumisial) {
        setFormData({
          nombre: lumisial.nombre,
          provincia_eclesiastica_id: lumisial.provincia_eclesiastica_id,
          diocesis_id: lumisial.diocesis_id,
          activo: lumisial.activo
        });
        // Pre-seleccionar estado y ciudad si existen
        if (lumisial.estado_region_iso2) {
          setSelectedState({ id: '', name: lumisial.estado_region || '', iso2: lumisial.estado_region_iso2 });
        } else {
          setSelectedState(null);
        }
        if (lumisial.ciudad_id) {
          setSelectedCity({ id: lumisial.ciudad_id, name: lumisial.ciudad || '' });
        } else {
          setSelectedCity(null);
        }
      } else {
        setFormData({ nombre: '', provincia_eclesiastica_id: '', diocesis_id: '', activo: true });
        setSelectedState(null);
        setSelectedCity(null);
      }
    }
  }, [isOpen, lumisial]);

  // Cargar diócesis cuando cambia la provincia
  useEffect(() => {
    if (formData.provincia_eclesiastica_id) {
      cargarDiocesis(formData.provincia_eclesiastica_id);
    } else {
      setDiocesisList([]);
      if (!lumisial) {
        setFormData(prev => ({ ...prev, diocesis_id: '' }));
        setSelectedState(null);
        setSelectedCity(null);
      }
    }
  }, [formData.provincia_eclesiastica_id]);

  const cargarProvincias = async () => {
    try {
      setLoadingProvincias(true);
      const data = await provinciaEclesiasticaService.listarActivos();
      setProvinciasEclesiasticas(data);
    } catch (err) {
      console.error('Error al cargar provincias:', err);
      reportarErrorInesperado(err, 'Error al cargar provincias eclesiásticas');
    } finally {
      setLoadingProvincias(false);
    }
  };

  const cargarDiocesis = async (provinciaId: string) => {
    try {
      setLoadingDiocesis(true);
      const data = await diocesisService.listarPorProvinciaEclesiastica(provinciaId);
      setDiocesisList(data);
      if (data.length > 0 && !lumisial) {
        setFormData(prev => ({ ...prev, diocesis_id: data[0].id }));
      }
    } catch (err) {
      console.error('Error al cargar diócesis:', err);
      reportarErrorInesperado(err, 'Error al cargar diócesis');
    } finally {
      setLoadingDiocesis(false);
    }
  };

  const handleProvinciaChange = (value: string) => {
    setFormData({ ...formData, provincia_eclesiastica_id: value, diocesis_id: '' });
    setSelectedState(null);
    setSelectedCity(null);
  };

  const handleDiocesisChange = (value: string) => {
    setFormData({ ...formData, diocesis_id: value });
    setSelectedState(null);
    setSelectedCity(null);
  };

  const handleStateChange = (state: State | null) => {
    setSelectedState(state);
    setSelectedCity(null);
  };

  const handleCityChange = (city: City | null) => {
    setSelectedCity(city);
  };

  const validate = (): boolean => {
    if (!formData.nombre.trim()) { handleError('El nombre del lumisial es requerido'); return false; }
    if (!formData.provincia_eclesiastica_id) { handleError('Debe seleccionar una provincia eclesiástica'); return false; }
    if (!formData.diocesis_id) { handleError('Debe seleccionar una diócesis o prefectura'); return false; }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    try {
      setLoading(true);

      const lumisialData = {
        nombre: formData.nombre,
        ciudad: selectedCity?.name || '',
        region: selectedState?.name || undefined,
        pais: selectedProvincia?.nombre || undefined,
        pais_iso2: selectedProvincia?.codigo_iso || undefined,
        estado_region: selectedState?.name || undefined,
        estado_region_iso2: selectedState?.iso2 || undefined,
        ciudad_id: selectedCity?.id || undefined,
        provincia_eclesiastica_id: formData.provincia_eclesiastica_id,
        diocesis_id: formData.diocesis_id,
        activo: formData.activo
      };

      if (lumisial) {
        await lumisialService.actualizar(lumisial.id, lumisialData);
      } else {
        await lumisialService.crear(lumisialData);
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error al guardar lumisial:', err);
      reportarErrorInesperado(err, `Error al ${lumisial ? 'actualizar' : 'crear'} el lumisial`);
    } finally {
      setLoading(false);
    }
  };

  // Para el StateSelector: si la diócesis tiene estados asignados, usar solo esos
  const stateCountryProp = useMemo(() => {
    if (!countryForSelectors) return null;
    return countryForSelectors;
  }, [countryForSelectors]);

  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      hasUnsavedChanges={hasFormChanges()}
      closeRef={closeRef}
      title={lumisial ? 'Editar Lumisial' : 'Agregar Lumisial'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* 1. Provincia Eclesiástica */}
        <div>
          <span className="block text-sm font-medium text-slate-700 mb-1">
            Provincia Eclesiástica *
          </span>
          {loadingProvincias ? (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700"></div>
            </div>
          ) : (
            <Select
              value={formData.provincia_eclesiastica_id}
              onChange={handleProvinciaChange}
              options={provinciasEclesiasticas.map(p => ({ value: p.id, label: p.nombre }))}
              placeholder="Seleccionar provincia eclesiástica"
            />
          )}
        </div>

        {/* 2. Diócesis */}
        <div>
          <span className="block text-sm font-medium text-slate-700 mb-1">
            Diócesis/Prefectura *
          </span>
          {loadingDiocesis && (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-700"></div>
            </div>
          )}
          {!loadingDiocesis && diocesisList.length === 0 && (
            <div className="text-sm text-slate-500 p-2 bg-slate-50 rounded-lg">
              {formData.provincia_eclesiastica_id ? 'No hay diócesis disponibles' : 'Primero seleccione una provincia eclesiástica'}
            </div>
          )}
          {!loadingDiocesis && diocesisList.length > 0 && (
            <Select
              value={formData.diocesis_id}
              onChange={handleDiocesisChange}
              options={diocesisList.map(d => ({
                value: d.id,
                label: `${d.nombre} (${d.tipo === 'diocesis' ? 'Diócesis' : 'Prefectura'})`
              }))}
              placeholder="Seleccionar diócesis o prefectura"
            />
          )}
        </div>

        {/* 3. Estado/Región */}
        {stateCountryProp && (
          <StateSelector
            country={stateCountryProp}
            value={selectedState}
            onChange={handleStateChange}
            label="Estado/Región"
            placeholder="Seleccionar estado/región..."
          />
        )}

        {/* 4. Ciudad */}
        {stateCountryProp && (
          <CitySelector
            country={stateCountryProp}
            state={selectedState}
            value={selectedCity}
            onChange={handleCityChange}
            label="Ciudad"
            placeholder="Seleccionar ciudad..."
          />
        )}

        {/* 5. Nombre */}
        <TextInput
          label="Nombre del Lumisial *"
          value={formData.nombre}
          onChange={(v) => setFormData({ ...formData, nombre: v })}
          placeholder="Ej: Gnosis Temuco"
        />

        {/* 6. Activo */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="activo"
            checked={formData.activo}
            onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
            className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="activo" className="ml-2 text-sm text-slate-700">
            Lumisial activo
          </label>
        </div>

        {lumisial?.cantidad_miembros !== undefined && (
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm text-slate-600">
              <span className="font-medium">Miembros asociados:</span> {lumisial.cantidad_miembros}
            </p>
          </div>
        )}

        <FormErrorAlert error={error} onClear={clearError} />

        <ModalFormActions
          onCancel={requestClose}
          loading={loading}
          disabled={loadingProvincias || loadingDiocesis}
          submitLabel={lumisial ? 'Actualizar' : 'Crear Lumisial'}
        />
      </form>
    </DialogShell>
  );
}
